import * as Popover from '@radix-ui/react-popover';
import { Building2, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import type { TenantFilterOption } from '../types';
import styles from './templates-toolbar.module.css';

interface TenantFilterMenuProps {
  tenantOptions: TenantFilterOption[];
  selectedTenantIds: string[];
  toggleTenant: (tenantId: string) => void;
  clearTenants: () => void;
  isLoading: boolean;
}

export function TenantFilterMenu({
  tenantOptions,
  selectedTenantIds,
  toggleTenant,
  clearTenants,
  isLoading,
}: TenantFilterMenuProps) {
  const [tenantSearch, setTenantSearch] = useState('');

  const selectedIdSet = new Set(selectedTenantIds);
  const normalizedTenantSearch = tenantSearch.trim().toLowerCase();
  const visibleTenantOptions = normalizedTenantSearch
    ? tenantOptions.filter(option => option.name.toLowerCase().includes(normalizedTenantSearch))
    : tenantOptions;

  const selectedCount = selectedTenantIds.length;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button type="button" className={styles.filterButton} data-active={selectedCount > 0}>
          <Building2 className={styles.filterIcon} />
          Tenant
          {selectedCount > 0 && <span className={styles.countPill}>{selectedCount}</span>}
          <ChevronDown className={styles.caret} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className={`${styles.menu} ${styles.menuWide}`} align="start" sideOffset={8}>
          <input
            type="text"
            className={styles.menuSearch}
            placeholder="Search tenants…"
            value={tenantSearch}
            onChange={event => setTenantSearch(event.target.value)}
          />
          <div className={styles.menuList}>
            {isLoading && <div className={styles.menuEmpty}>Loading tenants…</div>}
            {!isLoading && visibleTenantOptions.length === 0 && (
              <div className={styles.menuEmpty}>No tenants match</div>
            )}
            {!isLoading &&
              visibleTenantOptions.map(option => (
                <label key={option.id} className={styles.tenantOption}>
                  <input
                    type="checkbox"
                    checked={selectedIdSet.has(option.id)}
                    onChange={() => toggleTenant(option.id)}
                  />
                  <span className={styles.tenantName}>{option.name}</span>
                  <span className={styles.tenantCount}>{option.templateCount}</span>
                </label>
              ))}
          </div>
          {selectedCount > 0 && (
            <div className={styles.menuFooter}>
              <span>{selectedCount} selected</span>
              <button type="button" className={styles.clearButton} onClick={clearTenants}>
                Clear
              </button>
            </div>
          )}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
